import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Configuration for a single URL parameter
 */
export interface ParamConfig<T> {
  /** Default value when param is not in URL */
  default: T;
  /** Type for parsing - 'number' will parse as integer, 'boolean' will parse as boolean, default is 'string' */
  type?: 'string' | 'number' | 'boolean';
}

/**
 * Configuration object mapping param names to their configs
 */
export type ParamsConfig<T extends Record<string, unknown>> = {
  [K in keyof T]: ParamConfig<T[K]>;
};

/**
 * Return type for useUrlParams hook
 */
export interface UseUrlParamsReturn<T extends Record<string, unknown>> {
  /** Current parameter values (from URL or defaults) */
  params: T;
  /** Update multiple parameters at once */
  setParams: (params: Partial<T>) => void;
  /** Update a single parameter */
  setParam: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Reset all parameters to their defaults */
  resetParams: () => void;
}

/**
 * Parse a URL param value based on its type
 */
function parseValue<T>(
  value: string | null,
  config: ParamConfig<T>
): T {
  if (value === null) {
    return config.default;
  }

  if (config.type === 'number') {
    const parsed = parseInt(value, 10);
    return (isNaN(parsed) ? config.default : parsed) as T;
  }

  if (config.type === 'boolean') {
    return (value === 'true') as T;
  }

  return value as T;
}

/**
 * Check if a value equals the default value
 */
function isDefaultValue<T>(value: T, defaultValue: T): boolean {
  return value === defaultValue;
}

/**
 * Hook for bidirectional synchronization between React state and URL query parameters.
 *
 * Features:
 * - Reads initial values from URL query params
 * - Falls back to defaults if param not in URL
 * - Only writes non-default values to URL (keeps URL clean)
 * - Parses numbers correctly
 * - Uses react-router-dom's useSearchParams
 *
 * @example
 * ```tsx
 * const { params, setParam } = useUrlParams({
 *   search: { default: '' },
 *   page: { default: 1, type: 'number' },
 *   sortBy: { default: 'name' },
 *   sortOrder: { default: 'asc' as 'asc' | 'desc' },
 * });
 *
 * // params.search, params.page are typed correctly
 * // setParam('page', 2) updates URL to ?page=2
 * // setParam('page', 1) removes page from URL (it's the default)
 * ```
 */
export function useUrlParams<T extends Record<string, unknown>>(
  config: ParamsConfig<T>
): UseUrlParamsReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  // Keep a stable reference to config to avoid unnecessary re-renders
  const configRef = useRef(config);
  configRef.current = config;

  // Parse current URL params into typed object
  const params = useMemo(() => {
    const result = {} as T;
    const keys = Object.keys(configRef.current) as (keyof T)[];

    for (const key of keys) {
      const paramConfig = configRef.current[key];
      const urlValue = searchParams.get(String(key));
      result[key] = parseValue(urlValue, paramConfig);
    }

    return result;
  }, [searchParams]);

  // Update multiple params at once
  const setParams = useCallback((newParams: Partial<T>) => {
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      const keys = Object.keys(newParams) as (keyof T)[];

      for (const key of keys) {
        const value = newParams[key];
        const paramConfig = configRef.current[key];

        if (value === undefined) {
          continue;
        }

        // Remove param if it's the default value (keep URL clean)
        if (isDefaultValue(value, paramConfig.default)) {
          updated.delete(String(key));
        } else {
          updated.set(String(key), String(value));
        }
      }

      return updated;
    }, { replace: true });
  }, [setSearchParams]);

  // Update a single param
  const setParam = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setParams({ [key]: value } as unknown as Partial<T>);
  }, [setParams]);

  // Reset all params to defaults
  const resetParams = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return {
    params,
    setParams,
    setParam,
    resetParams,
  };
}
